/**
 * Notification settings configuration: the sections, their user-facing
 * titles and descriptions, and the per-channel rows rendered on the
 * notification settings screen. Centralized here so the copy and section
 * structure live in one place rather than inline in the screen component.
 *
 * `type` maps to a notification-preference event key (see
 * `NotificationPreferenceBodyV2['event']`); each row's `method` maps to a
 * channel. `value` is a render-time placeholder — the real toggle state is
 * resolved from the user's `notificationsPreferences` in the screen.
 */

export type NotificationChannelMethod = 'Push' | 'Email' | 'Sms';

export interface NotificationChannelRow {
  method: NotificationChannelMethod;
  value: boolean;
  /** Full label used as the switch's accessible name, e.g. "Push notifications". */
  title: string;
  /** Short label rendered as the matrix column header, e.g. "Push". */
  columnLabel: string;
}

export interface NotificationSettingsSection {
  title: string;
  description?: string;
  type: string;
  data: NotificationChannelRow[];
}

const CHANNEL_ROWS: NotificationChannelRow[] = [
  { method: 'Push', value: false, title: 'Push notifications', columnLabel: 'Push' },
  { method: 'Email', value: false, title: 'Email notifications', columnLabel: 'Email' },
  { method: 'Sms', value: false, title: 'Text notifications', columnLabel: 'Text' },
];

/**
 * Channel column headers for the notification settings matrix. Order matches
 * each section's `data` so a header aligns with the toggles in its column.
 */
export const NOTIFICATION_CHANNEL_COLUMNS = CHANNEL_ROWS.map(({ method, columnLabel }) => ({
  method,
  columnLabel,
}));

export const NOTIFICATION_SETTINGS_SECTIONS: NotificationSettingsSection[] = [
  {
    title: 'New inquiries',
    description: 'Get notified when a new inquiry is sent to you',
    type: 'newInquiry',
    data: CHANNEL_ROWS,
  },
  {
    title: 'New messages',
    description: 'Get notified when you receive a new message in an inquiry',
    type: 'newMessage',
    data: CHANNEL_ROWS,
  },
  {
    title: 'References ready',
    description: 'Get notified when references you requested are ready to review',
    type: 'referencesReady',
    data: CHANNEL_ROWS,
  },
  {
    title: 'Community notifications',
    description: 'Get notified about likes, comments, and replies in the Inquiry Library',
    type: 'communityNotifications',
    data: CHANNEL_ROWS,
  },
];

/**
 * Section types only relevant to experts (recipients) — submitters never
 * receive inquiries or request references — so they are hidden from
 * non-expert users.
 */
export const EXPERT_ONLY_NOTIFICATION_SECTION_TYPES = ['newInquiry', 'referencesReady'];

/**
 * Screen-level copy for the notification settings screen. Section titles and
 * descriptions live in `NOTIFICATION_SETTINGS_SECTIONS` above; this covers the
 * intro, the channel-setup alerts (no mobile number, push permission off), and
 * their buttons / accessibility labels.
 */
export const NOTIFICATION_SETTINGS_COPY = {
  screenTitle: 'Notification Settings',
  intro:
    'Select how you would like to receive notifications. Your profile must have a mobile number in order to receive text notifications.',
  noMobileNumber: 'No mobile number on file.',
  addNumberLabel: 'Add number',
  addNumberA11yLabel: 'Add mobile number',
  smsDisabledHint: 'Add a mobile number to your profile to enable text notifications',
  pushDisabledHint: 'Enable push notifications in your device settings',
  pushOffPromptable: "Push notifications aren't turned on for this device yet.",
  pushOffBlocked:
    'Push notifications are blocked in your browser settings. Turn them on there to use them here.',
  turnOnPushLabel: 'Turn on',
  turnOnPushA11yLabel: 'Turn on push notifications',
  /*
   * Names the browser rather than the app because that is where the cause
   * almost always is, and stops short of telling anyone to change a setting:
   * a content blocker is the usual reason and no notification setting fixes
   * it. Says email still works, since it does, and this is the moment someone
   * would otherwise assume they have gone unreachable.
   */
  pushUnavailableTitle: "Couldn't turn on push notifications",
  pushUnavailableMessage:
    "Something in this browser is blocking them. You'll still get email notifications.",
} as const;
